import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Grid3x3, Layers, Building2, FileText, Mail, Users, Settings,
  LogOut, Plus, Trash2, Upload, X, Save, MapPin,
} from "lucide-react";
import { api, apiErr, assetUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { STATUS_LIST, STATUS_LABELS, USE_LIST, USE_LABELS, PHASES, STATUS_COLORS } from "../../lib/constants";
import HotspotPlan from "../../components/site/HotspotPlan";

const NAV = [
  ["overview", "Overview", LayoutDashboard],
  ["parcels", "Parcels", Grid3x3],
  ["stages", "Stages", Layers],
  ["pins", "Plan Pins", MapPin],
  ["builders", "Builders", Building2],
  ["sections", "Sections", Settings],
  ["documents", "Documents", FileText],
  ["enquiries", "Enquiries", Mail],
  ["users", "Users", Users],
  ["project", "Project", Settings],
];

const Field = ({ label, children }) => (
  <label className="block mb-3">
    <span className="text-[0.6rem] uppercase tracking-wide text-[#F3EEE3]/50">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const inputCls = "w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-lg px-3 py-2 text-[#F3EEE3] text-sm focus:border-[#C9B79C] outline-none";

const Modal = ({ title, onClose, children, onSave, busy }) => (
  <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-[#0C1510]/80" onClick={onClose} />
    <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#12201A] border border-[#F3EEE3]/15 rounded-2xl p-7">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl text-[#F3EEE3]">{title}</h3>
        <button onClick={onClose} className="text-[#F3EEE3]/60 hover:text-[#F3EEE3]"><X size={20} /></button>
      </div>
      {children}
      {onSave && (
        <button onClick={onSave} disabled={busy} data-testid="modal-save" className="mt-4 inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium hover:bg-white disabled:opacity-60">
          <Save size={15} /> {busy ? "Saving…" : "Save"}
        </button>
      )}
    </div>
  </div>
);

const csv = (a) => (Array.isArray(a) ? a.join(", ") : a || "");
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const num = (v) => (v === "" || v == null ? null : Number(v));

// ---------------- Overview ----------------
const Overview = () => {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    ["Total parcels", s.total_parcels], ["Available", s.available], ["Reserved", s.reserved],
    ["Sold", s.sold], ["Sold %", s.sold_percent != null ? `${s.sold_percent}%` : "—"], ["Stages", s.stages],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {cards.map(([l, v]) => (
        <div key={l} className="rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
          <p className="font-display text-3xl text-[#F3EEE3]">{v ?? "—"}</p>
          <p className="eyebrow text-[0.55rem] text-[#C9B79C] mt-1">{l}</p>
        </div>
      ))}
    </div>
  );
};

// ---------------- Parcels ----------------
const emptyParcel = { parcel_no: "", stage_id: "", area_sqft: "", dimensions: "", status: "available", land_use: "residential", price: "", price_display: "", utilities: "", readiness: "", buildable_types: "", zoning: "", notes: "" };
const Parcels = () => {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/parcels").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/stages").then((r) => setStages(r.data)); }, [load]);

  const open = (p) => setEdit(p ? { ...p, utilities: csv(p.utilities), buildable_types: csv(p.buildable_types), area_sqft: p.area_sqft ?? "", price: p.price ?? "" } : { ...emptyParcel });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...edit, area_sqft: num(edit.area_sqft), price: num(edit.price), utilities: toArr(edit.utilities), buildable_types: toArr(edit.buildable_types), stage_id: edit.stage_id || null };
      delete body._id;
      if (edit.id) await api.put(`/parcels/${edit.id}`, body); else await api.post("/parcels", body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete parcel?")) return; await api.delete(`/parcels/${id}`); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-[#F3EEE3]/60 text-sm">{items.length} parcels</p>
        <button onClick={() => open(null)} data-testid="add-parcel" className="inline-flex items-center gap-2 bg-[#C9B79C] text-[#12201A] px-4 py-2 rounded-full text-sm font-medium"><Plus size={15} /> Add parcel</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#F3EEE3]/12">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#F3EEE3]/50 text-xs border-b border-[#F3EEE3]/12">
            <th className="px-4 py-3">Parcel</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Use</th><th className="px-4 py-3">Area</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[#F3EEE3]/8">
                <td className="px-4 py-3 text-[#F3EEE3]">{p.parcel_no}</td>
                <td className="px-4 py-3 text-[#F3EEE3]/70">{STATUS_LABELS[p.status]}</td>
                <td className="px-4 py-3 text-[#F3EEE3]/70">{USE_LABELS[p.land_use]}</td>
                <td className="px-4 py-3 text-[#F3EEE3]/70">{p.area_sqft || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => open(p)} className="text-[#C9B79C] text-xs mr-4">Edit</button>
                  <button onClick={() => del(p.id)} className="text-[#8A4B4B]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit parcel" : "New parcel"} onClose={() => setEdit(null)} onSave={save} busy={busy}>
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Parcel no"><input className={inputCls} value={edit.parcel_no} onChange={(e) => setEdit({ ...edit, parcel_no: e.target.value })} data-testid="parcel-no" /></Field>
            <Field label="Stage"><select className={inputCls} value={edit.stage_id || ""} onChange={(e) => setEdit({ ...edit, stage_id: e.target.value })}><option value="">—</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
            <Field label="Status"><select className={inputCls} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{STATUS_LIST.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}</select></Field>
            <Field label="Land use"><select className={inputCls} value={edit.land_use} onChange={(e) => setEdit({ ...edit, land_use: e.target.value })}>{USE_LIST.map((s) => <option key={s} value={s}>{USE_LABELS[s]}</option>)}</select></Field>
            <Field label="Area (sq ft)"><input className={inputCls} value={edit.area_sqft} onChange={(e) => setEdit({ ...edit, area_sqft: e.target.value })} /></Field>
            <Field label="Dimensions"><input className={inputCls} value={edit.dimensions || ""} onChange={(e) => setEdit({ ...edit, dimensions: e.target.value })} /></Field>
            <Field label="Price ($)"><input className={inputCls} value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} /></Field>
            <Field label="Price display"><input className={inputCls} value={edit.price_display || ""} onChange={(e) => setEdit({ ...edit, price_display: e.target.value })} placeholder="Contact for pricing" /></Field>
            <Field label="Zoning"><input className={inputCls} value={edit.zoning || ""} onChange={(e) => setEdit({ ...edit, zoning: e.target.value })} /></Field>
            <Field label="Readiness"><input className={inputCls} value={edit.readiness || ""} onChange={(e) => setEdit({ ...edit, readiness: e.target.value })} /></Field>
            <Field label="Utilities (comma)"><input className={inputCls} value={edit.utilities} onChange={(e) => setEdit({ ...edit, utilities: e.target.value })} /></Field>
            <Field label="Buildable types (comma)"><input className={inputCls} value={edit.buildable_types} onChange={(e) => setEdit({ ...edit, buildable_types: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><textarea className={inputCls} rows={2} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Field>
          <p className="text-[0.6rem] text-[#F3EEE3]/40">Geometry (polygon) is set by GeoJSON import; existing geometry is preserved on edit.</p>
        </Modal>
      )}
    </div>
  );
};

// ---------------- Stages ----------------
const Stages = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/stages").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const open = (s) => setEdit(s ? { ...s, works_done: csv(s.works_done), works_planned: csv(s.works_planned) } : { number: items.length + 1, name: `Stage ${items.length + 1}`, status: "planning", phase: "Planning", area: "", description: "", works_done: "", works_planned: "", timeline_note: "", order: items.length });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...edit, number: num(edit.number), order: num(edit.order) || 0, works_done: toArr(edit.works_done), works_planned: toArr(edit.works_planned), parcels_count: null, geometry: edit.geometry || null };
      delete body._id;
      if (edit.id) await api.put(`/stages/${edit.id}`, body); else await api.post("/stages", body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete stage?")) return; await api.delete(`/stages/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-between items-center mb-5"><p className="text-[#F3EEE3]/60 text-sm">{items.length} stages</p>
        <button onClick={() => open(null)} className="inline-flex items-center gap-2 bg-[#C9B79C] text-[#12201A] px-4 py-2 rounded-full text-sm font-medium"><Plus size={15} /> Add stage</button></div>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-5 py-4">
            <span className="text-[#F3EEE3]">{s.name} <span className="text-[#F3EEE3]/40 text-xs ml-2">{s.phase} · {s.area}</span></span>
            <span><button onClick={() => open(s)} className="text-[#C9B79C] text-xs mr-4">Edit</button><button onClick={() => del(s.id)} className="text-[#8A4B4B]"><Trash2 size={14} /></button></span>
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit stage" : "New stage"} onClose={() => setEdit(null)} onSave={save} busy={busy}>
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Number"><input className={inputCls} value={edit.number} onChange={(e) => setEdit({ ...edit, number: e.target.value })} /></Field>
            <Field label="Name"><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Status"><select className={inputCls} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{["planning","approvals","servicing","construction","available","completed"].map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Phase"><select className={inputCls} value={edit.phase} onChange={(e) => setEdit({ ...edit, phase: e.target.value })}>{PHASES.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Area"><input className={inputCls} value={edit.area || ""} onChange={(e) => setEdit({ ...edit, area: e.target.value })} /></Field>
            <Field label="Timeline note"><input className={inputCls} value={edit.timeline_note || ""} onChange={(e) => setEdit({ ...edit, timeline_note: e.target.value })} /></Field>
          </div>
          <Field label="Description"><textarea className={inputCls} rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
          <Field label="Works done (comma)"><input className={inputCls} value={edit.works_done} onChange={(e) => setEdit({ ...edit, works_done: e.target.value })} /></Field>
          <Field label="Works planned (comma)"><input className={inputCls} value={edit.works_planned} onChange={(e) => setEdit({ ...edit, works_planned: e.target.value })} /></Field>
        </Modal>
      )}
    </div>
  );
};

// ---------------- Uploader ----------------
const useUpload = () => async (file, endpoint = "/assets/upload", extra = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(extra).forEach(([k, v]) => v != null && fd.append(k, v));
  const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

// ---------------- Builders ----------------
const Builders = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = useUpload();
  const load = useCallback(() => api.get("/builders").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const open = (b) => setEdit(b ? { ...b, specialties: csv(b.specialties) } : { name: "", locale: "", website: "", email: "", phone: "", instagram: "", tagline: "", description: "", specialties: "", logo_url: "", gallery: [], models: [], featured: true, order: items.length });
  const save = async () => {
    setBusy(true);
    try { const body = { ...edit, order: num(edit.order) || 0, specialties: toArr(edit.specialties) }; delete body._id;
      if (edit.id) await api.put(`/builders/${edit.id}`, body); else await api.post("/builders", body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete builder?")) return; await api.delete(`/builders/${id}`); load(); };
  const onLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await upload(f); setEdit({ ...edit, logo_url: r.url }); toast.success("Logo uploaded"); } catch (err) { toast.error(apiErr(err)); } };
  const onGallery = async (e) => { const files = [...(e.target.files || [])]; for (const f of files) { try { const r = await upload(f); setEdit((cur) => ({ ...cur, gallery: [...(cur.gallery || []), r.url] })); } catch (err) { toast.error(apiErr(err)); } } toast.success("Gallery updated"); };
  const removeImg = (i) => setEdit((cur) => ({ ...cur, gallery: cur.gallery.filter((_, idx) => idx !== i) }));
  return (
    <div>
      <div className="flex justify-between items-center mb-5"><p className="text-[#F3EEE3]/60 text-sm">{items.length} builders</p>
        <button onClick={() => open(null)} className="inline-flex items-center gap-2 bg-[#C9B79C] text-[#12201A] px-4 py-2 rounded-full text-sm font-medium"><Plus size={15} /> Add builder</button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-5 py-4">
            <span className="flex items-center gap-3 text-[#F3EEE3]">{b.logo_url && <img src={assetUrl(b.logo_url)} className="h-8 w-14 object-contain" alt={`${b.name} logo`} />}{b.name}</span>
            <span><button onClick={() => open(b)} className="text-[#C9B79C] text-xs mr-4">Edit</button><button onClick={() => del(b.id)} className="text-[#8A4B4B]"><Trash2 size={14} /></button></span>
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit builder" : "New builder"} onClose={() => setEdit(null)} onSave={save} busy={busy}>
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Name"><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Locale"><input className={inputCls} value={edit.locale || ""} onChange={(e) => setEdit({ ...edit, locale: e.target.value })} /></Field>
            <Field label="Website"><input className={inputCls} value={edit.website || ""} onChange={(e) => setEdit({ ...edit, website: e.target.value })} /></Field>
            <Field label="Instagram"><input className={inputCls} value={edit.instagram || ""} onChange={(e) => setEdit({ ...edit, instagram: e.target.value })} /></Field>
            <Field label="Email"><input className={inputCls} value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></Field>
            <Field label="Order"><input className={inputCls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} /></Field>
            <Field label="Featured">
              <select className={inputCls} value={edit.featured ? "yes" : "no"} onChange={(e) => setEdit({ ...edit, featured: e.target.value === "yes" })}>
                <option value="yes">Yes</option><option value="no">No</option>
              </select>
            </Field>
          </div>
          <Field label="Tagline"><input className={inputCls} value={edit.tagline || ""} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} /></Field>
          <Field label="Description"><textarea className={inputCls} rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
          <Field label="Specialties (comma separated)"><input className={inputCls} value={edit.specialties || ""} onChange={(e) => setEdit({ ...edit, specialties: e.target.value })} placeholder="Custom homes, Spec homes" /></Field>
          <Field label="Logo">
            <div className="flex items-center gap-3">
              {edit.logo_url && <img src={assetUrl(edit.logo_url)} className="h-10 w-20 object-contain bg-[#0C1510] rounded" alt={`${edit.name || "Builder"} logo preview`} />}
              <label className="inline-flex items-center gap-2 border border-[#F3EEE3]/20 text-[#F3EEE3] px-3 py-2 rounded-lg text-sm cursor-pointer"><Upload size={14} /> Upload<input type="file" accept="image/*" className="hidden" onChange={onLogo} /></label>
            </div>
          </Field>
          <Field label="Gallery">
            <div className="flex flex-wrap gap-2 mb-2">
              {(edit.gallery || []).map((g, i) => (
                <div key={i} className="relative group">
                  <img src={assetUrl(g)} className="h-16 w-20 object-cover rounded" alt={`${edit.name || "Builder"} gallery photo ${i + 1}`} />
                  <button type="button" onClick={() => removeImg(i)} className="absolute -top-1.5 -right-1.5 bg-[#8A4B4B] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 border border-[#F3EEE3]/20 text-[#F3EEE3] px-3 py-2 rounded-lg text-sm cursor-pointer"><Upload size={14} /> Add photos<input type="file" accept="image/*" multiple className="hidden" onChange={onGallery} /></label>
          </Field>
        </Modal>
      )}
    </div>
  );
};

// ---------------- Sections ----------------
const Sections = () => {
  const keys = [["commercial", "Commercial Centre"], ["apartment", "Apartment Project"], ["about", "About the Developer"], ["faq", "FAQ"]];
  const [key, setKey] = useState("commercial");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const upload = useUpload();
  useEffect(() => { api.get(`/sections/${key}`).then((r) => setData(r.data || {})); }, [key]);
  const save = async () => { setBusy(true); try { await api.put(`/sections/${key}`, data); toast.success("Saved"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const onImg = async (e) => { const f = e.target.files?.[0]; if (!f) return; const r = await upload(f); setData({ ...data, image_url: r.url }); toast.success("Image uploaded"); };
  return (
    <div>
      <div className="flex gap-2 mb-6">{keys.map(([k, l]) => <button key={k} onClick={() => setKey(k)} className={`px-3 py-1.5 rounded-full text-xs border ${key === k ? "border-[#C9B79C] bg-[#C9B79C]/15 text-[#F3EEE3]" : "border-[#F3EEE3]/15 text-[#F3EEE3]/55"}`}>{l}</button>)}</div>
      <div className="max-w-2xl">
        <Field label="Title"><input className={inputCls} value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} /></Field>
        <Field label="Status (e.g. Under construction / Approved / Planned)"><input className={inputCls} value={data.status || ""} onChange={(e) => setData({ ...data, status: e.target.value })} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={8} value={data.body || ""} onChange={(e) => setData({ ...data, body: e.target.value })} /></Field>
        <Field label="Image">
          <div className="flex items-center gap-3">{data.image_url && <img src={assetUrl(data.image_url)} className="h-14 rounded" alt={`${data.title || "Section"} image preview`} />}
            <label className="inline-flex items-center gap-2 border border-[#F3EEE3]/20 text-[#F3EEE3] px-3 py-2 rounded-lg text-sm cursor-pointer"><Upload size={14} /> Upload<input type="file" accept="image/*" className="hidden" onChange={onImg} /></label></div>
        </Field>
        <button onClick={save} disabled={busy} className="mt-2 inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium"><Save size={15} /> {busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

// ---------------- Documents ----------------
const DocsAdmin = () => {
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [form, setForm] = useState({ title: "", category: "general", visibility: "public", stage_id: "", builder_id: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = useUpload();
  const load = useCallback(() => api.get("/documents").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/stages").then((r) => setStages(r.data)); api.get("/builders").then((r) => setBuilders(r.data)); }, [load]);
  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      await upload(file, "/documents", { title: form.title || file.name, category: form.category, visibility: form.visibility, stage_id: form.stage_id || "", builder_id: form.builder_id || "" });
      toast.success("Uploaded"); setForm({ title: "", category: "general", visibility: "public", stage_id: "", builder_id: "" }); setFile(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete document?")) return; await api.delete(`/documents/${id}`); load(); };
  const cats = ["master_plan","parcel_plan","zoning","servicing","schedule","price_list","guidelines","disclosure","presentation","commercial","apartment","general"];
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6">
        <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-4">Upload document</p>
        <Field label="Title"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" /></Field>
        <div className="grid grid-cols-2 gap-x-4">
          <Field label="Category"><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{cats.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Visibility"><select className={inputCls} value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}><option value="public">public</option><option value="confidential">confidential</option></select></Field>
          <Field label="Stage (optional)"><select className={inputCls} value={form.stage_id} onChange={(e) => setForm({ ...form, stage_id: e.target.value })}><option value="">—</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          <Field label="Builder (optional)"><select className={inputCls} value={form.builder_id} onChange={(e) => setForm({ ...form, builder_id: e.target.value })}><option value="">—</option>{builders.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Field>
        </div>
        <label className="mt-2 flex items-center gap-2 border border-[#F3EEE3]/20 text-[#F3EEE3] px-3 py-2 rounded-lg text-sm cursor-pointer w-fit"><Upload size={14} /> {file ? file.name : "Choose file"}<input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file" /></label>
        <button onClick={submit} disabled={busy} data-testid="doc-upload" className="mt-4 inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium"><Upload size={15} /> {busy ? "Uploading…" : "Upload"}</button>
      </div>
      <div>
        <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-4">{items.length} documents</p>
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-4 py-3">
              <span className="text-[#F3EEE3] text-sm">{d.title} <span className="text-[#F3EEE3]/40 text-xs ml-1">· {d.category} · {d.visibility}</span></span>
              <button onClick={() => del(d.id)} className="text-[#8A4B4B]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------- Enquiries ----------------
const Enquiries = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/enquiries").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.patch(`/enquiries/${id}`, { status }); load(); };
  return (
    <div className="space-y-3">
      {items.map((e) => (
        <div key={e.id} className="rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
          <div className="flex items-center justify-between">
            <div><p className="text-[#F3EEE3] font-medium">{e.name} <span className="text-[#F3EEE3]/50 text-sm ml-2">{e.email}</span></p>
              <p className="text-[#F3EEE3]/50 text-xs mt-1">{e.phone || "—"} · {new Date(e.created_at).toLocaleString()} · {e.source}</p></div>
            <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className={inputCls + " w-auto"}><option value="new">new</option><option value="contacted">contacted</option><option value="closed">closed</option></select>
          </div>
          {e.message && <p className="text-[#F3EEE3]/70 text-sm mt-3">{e.message}</p>}
        </div>
      ))}
      {!items.length && <p className="text-[#F3EEE3]/40">No enquiries yet.</p>}
    </div>
  );
};

// ---------------- Users ----------------
const UsersAdmin = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "investor" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/auth/users").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { setBusy(true); try { await api.post("/auth/register", form); toast.success("User created"); setForm({ name: "", email: "", password: "", role: "investor" }); load(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const del = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/auth/users/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6">
        <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-4">Create investor account</p>
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Email"><input className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Password"><input className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
        <Field label="Role"><select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="investor">investor</option><option value="admin">admin</option></select></Field>
        <button onClick={create} disabled={busy} className="mt-2 inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium"><Plus size={15} /> Create</button>
      </div>
      <div className="space-y-2">
        {items.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-4 py-3">
            <span className="text-[#F3EEE3] text-sm">{u.name} <span className="text-[#F3EEE3]/50">· {u.email}</span> <span className="text-[#C9B79C] text-xs ml-1">{u.role}</span></span>
            {u.role !== "admin" && <button onClick={() => del(u.id)} className="text-[#8A4B4B]"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- Project ----------------
const Project = () => {
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/project").then((r) => setData(r.data || {})); }, []);
  const save = async () => { setBusy(true); try { await api.put("/project", data); toast.success("Saved"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const F = (k, l) => <Field label={l}><input className={inputCls} value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} /></Field>;
  return (
    <div className="max-w-2xl">
      <div className="grid grid-cols-2 gap-x-4">
        {F("name", "Name")}{F("location", "Location")}{F("tagline", "Tagline")}{F("total_area", "Total area")}
        {F("distance_to_city", "Distance to city")}{F("infra_start", "Infra start")}{F("contact_email", "Contact email")}{F("contact_phone", "Contact phone")}
      </div>
      <Field label="Subtitle"><textarea className={inputCls} rows={2} value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} /></Field>
      <Field label="Description"><textarea className={inputCls} rows={3} value={data.description || ""} onChange={(e) => setData({ ...data, description: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-[#F3EEE3]/70 text-sm mb-4"><input type="checkbox" checked={!!data.is_demo} onChange={(e) => setData({ ...data, is_demo: e.target.checked })} /> Show "demo data" badge</label>
      <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium"><Save size={15} /> {busy ? "Saving…" : "Save"}</button>
    </div>
  );
};

// ---------------- Plan Pins ----------------
const PlanPins = () => {
  const [target, setTarget] = useState("lots");   // "lots" | "stages"
  const [stages, setStages] = useState([]);
  const [project, setProject] = useState({});
  const [stageId, setStageId] = useState("");
  const [items, setItems] = useState([]);          // lots or stages
  const [coords, setCoords] = useState({});
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);

  useEffect(() => {
    api.get("/stages").then((r) => {
      setStages(r.data);
      const active = r.data.filter((s) => s.plan_image).find((s) => s.status === "available") || r.data.find((s) => s.plan_image);
      if (active) setStageId(active.id);
    });
    api.get("/project").then((r) => setProject(r.data || {}));
  }, []);

  const initCoords = (arr) => {
    const c = {};
    arr.forEach((p) => { if (p.hotspot && typeof p.hotspot.x === "number") c[p.id] = { x: p.hotspot.x, y: p.hotspot.y }; });
    setCoords(c); setSelected(null);
  };

  // load items when target/stage changes
  useEffect(() => {
    if (target === "stages") {
      const arr = [...stages].sort((a, b) => a.number - b.number);
      setItems(arr); initCoords(arr);
    } else if (stageId) {
      api.get(`/parcels?stage_id=${stageId}`).then((r) => {
        const sorted = [...r.data].sort((a, b) => (a.lot_number || 0) - (b.lot_number || 0));
        setItems(sorted); initCoords(sorted);
      });
    }
    // eslint-disable-next-line
  }, [target, stageId, stages]);

  const planSrc = target === "stages" ? project.site_plan_image : stages.find((s) => s.id === stageId)?.plan_image;
  const labelOf = (p) => String(target === "stages" ? p.number : (p.lot_number ?? p.parcel_no));
  const pins = items.filter((p) => coords[p.id]).map((p) => ({
    id: p.id, x: coords[p.id].x, y: coords[p.id].y, label: labelOf(p), color: STATUS_COLORS[p.status] || "#C9B79C",
  }));

  const place = (x, y) => {
    if (!selected) { toast.error("Select an item first"); return; }
    setCoords((c) => ({ ...c, [selected]: { x, y } }));
    if (autoAdvance) {
      const idx = items.findIndex((l) => l.id === selected);
      const next = items.slice(idx + 1).find((l) => !coords[l.id] && l.id !== selected);
      setSelected(next ? next.id : null);
    }
  };
  const clearPin = (id) => setCoords((c) => { const n = { ...c }; delete n[id]; return n; });

  const save = async () => {
    setBusy(true);
    try {
      const hotspots = items.map((p) => coords[p.id] ? { id: p.id, x: coords[p.id].x, y: coords[p.id].y } : { id: p.id, x: -1, y: -1 });
      const url = target === "stages" ? "/stages/hotspots/bulk" : "/parcels/hotspots/bulk";
      await api.put(url, { hotspots });
      toast.success(`Saved ${Object.keys(coords).length} pins`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 p-1 rounded-full bg-[#0C1510] border border-[#F3EEE3]/12 w-fit mb-5">
        <button onClick={() => setTarget("lots")} data-testid="pins-target-lots" className={`px-4 py-2 rounded-full text-xs ${target === "lots" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/60"}`}>Stage lots</button>
        <button onClick={() => setTarget("stages")} data-testid="pins-target-stages" className={`px-4 py-2 rounded-full text-xs ${target === "stages" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/60"}`}>Master site plan (stages)</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        {target === "lots" && (
          <select value={stageId} onChange={(e) => setStageId(e.target.value)} data-testid="pins-stage-select" className={inputCls + " w-auto"}>
            {stages.filter((s) => s.plan_image).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 text-[#F3EEE3]/70 text-sm"><input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)} /> Auto-advance</label>
        <span className="text-[#F3EEE3]/50 text-sm">{Object.keys(coords).length}/{items.length} placed</span>
        <button onClick={save} disabled={busy || !planSrc} data-testid="pins-save" className="ml-auto inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2.5 rounded-full font-medium hover:bg-white disabled:opacity-60"><Save size={15} /> {busy ? "Saving…" : "Save pins"}</button>
      </div>

      {!planSrc ? (
        <p className="text-[#F3EEE3]/50 text-sm">No plan image available{target === "stages" ? " for the master site plan" : " for this stage"}.</p>
      ) : (
        <div className="grid lg:grid-cols-[1fr_260px] gap-5">
          <HotspotPlan src={planSrc} pins={pins} editable onPlace={place} onPinClick={(p) => setSelected(p.id)} selectedId={selected} height="h-[62vh]" />
          <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-4 h-[62vh] overflow-y-auto">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">{target === "stages" ? "Stages" : "Lots"} — select, then click plan</p>
            <div className="space-y-1.5">
              {items.map((p) => {
                const has = !!coords[p.id];
                const isSel = selected === p.id;
                return (
                  <div key={p.id} data-testid={`pins-item-${labelOf(p)}`} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer ${isSel ? "bg-[#C9B79C] text-[#12201A]" : "bg-[#0C1510] text-[#F3EEE3]/80 hover:bg-[#12201A]"}`} onClick={() => setSelected(p.id)}>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: has ? (STATUS_COLORS[p.status] || "#C9B79C") : "transparent", border: has ? "none" : "1px solid #ffffff40" }} />
                      {target === "stages" ? p.name : `Lot ${labelOf(p)}`}
                    </span>
                    {has && <button onClick={(e) => { e.stopPropagation(); clearPin(p.id); }} className={isSel ? "text-[#12201A]" : "text-[#8A4B4B]"}><Trash2 size={13} /></button>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <p className="text-[0.62rem] text-[#F3EEE3]/40 mt-4">Positions are set by you from the real plan and saved. Lot pins appear on the stage page; stage pins appear on the Master Plan site plan.</p>
    </div>
  );
};

const VIEWS = { overview: Overview, parcels: Parcels, stages: Stages, pins: PlanPins, builders: Builders, sections: Sections, documents: DocsAdmin, enquiries: Enquiries, users: UsersAdmin, project: Project };

const AdminApp = () => {
  const { user, loading, logout } = useAuth();
  const [view, setView] = useState("overview");
  if (loading) return <div className="pt-[120px] text-center text-[#F3EEE3]/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;
  const View = VIEWS[view];

  return (
    <div className="min-h-screen bg-[#12201A] flex">
      <aside className="w-56 shrink-0 border-r border-[#ffffff14] bg-[#0F1B15] p-5 flex flex-col">
        <p className="font-display text-xl text-[#F3EEE3] mb-1">Deer Park</p>
        <p className="eyebrow text-[0.5rem] text-[#F3EEE3]/45 mb-6">Admin</p>
        <nav className="space-y-1 flex-1">
          {NAV.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`admin-nav-${k}`} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${view === k ? "bg-[#C9B79C]/15 text-[#F3EEE3]" : "text-[#F3EEE3]/60 hover:text-[#F3EEE3]"}`}>
              <Icon size={16} className={view === k ? "text-[#C9B79C]" : ""} /> {l}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-2 text-[#F3EEE3]/60 hover:text-[#F3EEE3] text-sm px-3 py-2"><LogOut size={15} /> Sign out</button>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-8 py-10">
          <h1 className="font-display text-3xl text-[#F3EEE3] mb-8 capitalize">{view}</h1>
          <View />
        </div>
      </main>
    </div>
  );
};

export default AdminApp;
